import { useQuery } from "@tanstack/react-query";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import ConversationPanel from "@/components/lesson/ConversationPanel";
import LessonSidePanel from "@/components/lesson/LessonSidePanel";
import { useAudioRecording } from "@/hooks/useAudioRecording";
import { useLessonSession } from "@/hooks/useLessonSession";
import { useTimer } from "@/hooks/useTimer";
import { DEFAULT_LESSON_MINUTES } from "@/constants/ui";
import { api } from "@/services/api";

function speakLessonText(text) {
  const speechSynthesis = window.speechSynthesis;
  const Utterance = window.SpeechSynthesisUtterance;
  if (!speechSynthesis || !Utterance || !text) return;
  speechSynthesis.cancel();
  const utterance = new Utterance(text);
  utterance.lang = "en-US";
  utterance.rate = 0.92;
  speechSynthesis.speak(utterance);
}

export default function Lesson() {
  const { scenarioId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const { data: scenario, isLoading } = useQuery({ queryKey: ["scenario", scenarioId], queryFn: () => api.getScenario(scenarioId) });

  const lesson = useLessonSession({ scenario, initialSessionId: searchParams.get("session") });
  const isComplete = lesson.lessonProgress >= lesson.totalSteps;
  const { minutesLeft, secondsDisplay } = useTimer({
    minutes: scenario?.minutes || DEFAULT_LESSON_MINUTES,
    isRunning: !isLoading && Boolean(scenario) && !isComplete,
    resetKey: scenario?.id,
  });

  const audio = useAudioRecording({ disabled: isComplete, onTranscript: lesson.setDraft });

  if (isLoading || !scenario) {
    return <div className="mx-auto max-w-5xl px-4 py-8" data-testid="lesson-loading-state">Carregando aula...</div>;
  }

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8" data-testid="lesson-page">
      <button type="button" className="secondary-action mb-5" onClick={() => navigate("/scenarios")} data-testid="lesson-back-button">
        <ArrowLeft size={18} /> Voltar
      </button>
      <section className="lesson-grid" data-testid="lesson-layout">
        <LessonSidePanel scenario={scenario} currentStep={lesson.currentStep} totalSteps={lesson.totalSteps} minutesLeft={minutesLeft} secondsDisplay={secondsDisplay} />
        <ConversationPanel
          currentPrompt={lesson.currentPrompt}
          draft={lesson.draft}
          finalFeedback={lesson.finalFeedback}
          handleMicrophoneClick={audio.handleMicrophoneClick}
          handleMicrophoneTest={audio.handleMicrophoneTest}
          isComplete={isComplete}
          isListening={audio.isListening}
          isTestingMicrophone={audio.isTestingMicrophone}
          isTranscribing={audio.isTranscribing}
          messages={lesson.messages}
          setDraft={lesson.setDraft}
          speak={speakLessonText}
          speechRecognitionAvailable={audio.speechRecognitionAvailable}
          speechStatus={audio.speechStatus}
          submitTurn={lesson.submitTurn}
          supportsAudioRecording={audio.supportsAudioRecording}
          turnPending={lesson.turnPending}
        />
      </section>
    </div>
  );
}